import { Subscription, BehaviorSubject, Observable } from "rxjs";
import { FilterDescriptor } from "../data-table.component";
import { filterNormalized } from "./filter.fun";
import { Key, NormalizedItem, PageDescriptor, ProviderOptions, SortDescriptor, ITableDataSource } from "./model";


export class Normalizer<S = any, N = any> {

    private _normalized$ = new BehaviorSubject<N[]>(undefined);
    normalized$ = this._normalized$.asObservable();
    get normalized(): N[] { return this._normalized$.value }
    set normalized(v: N[]) { this._normalized$.next(v); }


    private _src: S[];
    get src(): S[] { return this._src }
    normalize: (item: S) => N;

    constructor(public readonly data$: Observable<any[]>) {

    }

    init(normalize: (item: S) => N) {
        this.normalize = normalize;
        this._sub = this.data$.subscribe(data => {
            this._src = data;
            this.normalized = this._normalize(data);
        })
    }

    _cache = new SmartMap<N>();
    protected _normalize(data: S[]): N[] {
        return data.map(x => {
            let n = this._cache.get(x);
            if (!n) {
                n = this.normalize(x)
                this._cache.set(x, n);
            }
            return n;
        });
    }

    private _sub: Subscription;
    destroy() { this._sub.unsubscribe(); }
}

export class DataAdapter<T = any> extends Normalizer<T, NormalizedItem<T>> {


    constructor(
        readonly dataSource: ITableDataSource<T>,
        readonly keyProperty?: keyof T, // what if ['item1','item2',...] then no need for key, display,value and img
        readonly displayProperty?: Key<T>,
        readonly valueProperty?: Key<T>,
        readonly imageProperty?: Key<T>,
        readonly termsProperty?: Key<T>,
        readonly options?: ProviderOptions<T>) {
        super(dataSource.data$);


        if (options) {
            if (options.page) this.dataSource.page = options.page;
            if (options.filter) this.dataSource.filter = options.filter;
            if (options.sort) this.dataSource.sort = options.sort;
            if (options.terms) this.dataSource.terms = options.terms;
        }

        this.dataSource.init();
        this.init(this._normalizeItem);

        this.dataSource.init();

        this._sub2 = dataSource.data$.subscribe(() => this._allNormalized = null);
    }



    _normalizeItem(item): NormalizedItem<T> {
        // if (this.groups) {
        //   for (let i = 0; i < this.groups.length; i++) {
        //     const n = this._normalize(this.groups[i].items);
        //     normalized.push(...n);
        //   }
        // }

        const key = this.keyProperty ? item[this.keyProperty] ?? item : item;
        const display = this.extract(item, this.displayProperty, item);
        let valueProperty = Array.isArray(this.valueProperty) ? [this.keyProperty, ...this.valueProperty] : [this.keyProperty, this.valueProperty]

        // if item is primitive type like string then no need to extract.
        let value = item as any;
        const valueProps = valueProperty.filter((v, i) => v && valueProperty.indexOf(v) == i);
        if (valueProps.length > 1) value = this.extract(item, valueProperty, item);
        else if (valueProps.length == 1) value = item[valueProps[0]];

        const image = this.extract(item, this.imageProperty, undefined);
        return { key, display, value, image, item };
    }

    extract(item: T, key: Key<T>, fallback: Partial<T>): Partial<T> {
        if (key) {
            if (Array.isArray(key)) {
                if (key.length == 1) return item[key[0]];
                const result: Partial<T> = {};
                key.forEach(k => result[k] = item[k]);
                return result;
            }
            else return item[key]
        }
        else return fallback;
    }

    normalizeFilter(q: string) {
        if (!q) return null;
        const filter: FilterDescriptor = {};
        const terms = q.split(' ').filter(x => x);
        terms.forEach(t => {
            if (t.indexOf(':') > 0) {
                const [key, value] = t.split(':');
                filter[key] = value;
            } else {
                if (!filter.terms) filter.terms = [];
                filter.terms.push(t);
            }
        });
        return filter;
    }


    private _sub2: Subscription;
    destroy() {
        super.destroy();
        this._sub2.unsubscribe();
        if (this.dataSource.destroy) this.dataSource.destroy();
    }


    get page(): PageDescriptor { return this.dataSource.page; }
    set page(page: PageDescriptor) { this.dataSource.page = page; }

    get sort(): SortDescriptor { return this.dataSource.sort; }
    set sort(sort: SortDescriptor) { this.dataSource.sort = sort; }


    get filter(): FilterDescriptor { return this.dataSource.filter; }
    set filter(filter: FilterDescriptor) { this.dataSource.filter = filter; }


    _allNormalized: NormalizedItem<T>[];
    refresh() {
        if (this.dataSource.allDataLoaded) {
            if (!this._allNormalized) this._allNormalized = this.normalized;
            this.normalized = filterNormalized(this._allNormalized, this.filter, this.sort, this.page, this.termsProperty ?? this.displayProperty);
        } else {
            this.dataSource.refresh({ page: this.page, sort: this.sort, filter: this.filter });
        }
    }





}

export class SmartMap<V> {
    _weak = new WeakMap<any, V>();
    _strong = new Map<object, V>();

    delete(key: any): boolean {
        const t = typeof key;
        if (t == 'object') return this._weak.delete(key);
        else return this._strong.delete(key);
    }
    get(key: any): V | undefined {
        const t = typeof key;
        if (t == 'object') return this._weak.get(key);
        else return this._strong.get(key);
    }
    has(key: any): boolean {
        const t = typeof key;
        if (t == 'object') return this._weak.has(key);
        else return this._strong.has(key);
    }
    set(key: any, value: V) {
        const t = typeof key;
        if (t == 'object') return this._weak.set(key, value);
        else return this._strong.set(key, value);
    }
}
